/**
 * flowAnimationUtils.js – Utilities for SCADA pipe flow animation.
 *
 * Provides helpers for calculating flow speed, direction, and state
 * based on connected node live values.
 */

/**
 * Calculate flow speed from a live value.
 * Higher values = faster flow animation.
 *
 * @param {number|null} liveValue - The current live reading from source node
 * @param {number} min - Min range of the parameter
 * @param {number} max - Max range of the parameter
 * @returns {number} Speed multiplier (0.3 = slow, 1 = normal, 3 = fast)
 */
export const calculateFlowSpeed = (liveValue, min = 0, max = 100) => {
  if (liveValue === null || liveValue === undefined) return 0;

  const val = Number(liveValue);
  if (isNaN(val) || val <= min) return 0;

  const range = max - min || 1;
  const pct = Math.min(1, Math.max(0, (val - min) / range));

  // Map percentage to speed: 0% → 0 (stopped), 50% → 1 (normal), 100% → 3 (fast)
  return pct * 3;
};

/**
 * Determine pipe edge state based on connected nodes.
 *
 * @param {object} sourceNode - Source SCADA node
 * @param {object} targetNode - Target SCADA node
 * @returns {{ active: boolean, warning: boolean, error: boolean, speed: number }}
 */
export const determinePipeState = (sourceNode) => {
  const sourceConfig = sourceNode?.data?.config || {};
  const sourceRunning = sourceConfig.isRunning !== false;
  const sourceOpen = sourceConfig.isOpen !== false;

  // Node is active if it's running/open
  const sourceActive = sourceRunning && sourceOpen;

  // Warning if any threshold exceeded
  const warning = sourceConfig.warningThreshold != null &&
    sourceNode?.data?.liveValue > sourceConfig.warningThreshold;

  // Error state
  const error = sourceConfig.criticalThreshold != null &&
    sourceNode?.data?.liveValue > sourceConfig.criticalThreshold;

  return {
    active: sourceActive,
    warning: !!warning,
    error: !!error,
    speed: sourceActive ? 1 : 0,
  };
};
